import React, { useState, useEffect, useRef } from "react";
import { FiX, FiCheck, FiSearch } from "react-icons/fi";
import ReactDOM from "react-dom";
import {
  whatsapp_list,
  sendTemplateApi,
  member_list,
  group_whatsapp_send
} from "../../../services/whatsapp/Service";
import { useInboxContext } from "./ConnectInboxContext";

// ContactSelectionList component for multi-select contacts
const ContactSelectionList = ({ 
  contacts, 
  selectedContactIds, 
  toggleContactSelection, 
  searchTerm, 
  setSearchTerm,
  loadMoreContacts,
  loading,
  onSelectAll,
  onUnselectAll
}) => {
  const contactListRef = useRef(null);
  
  const handleScroll = () => {
    if (contactListRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = contactListRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 20 && !loading) {
        loadMoreContacts();
      }
    }
  };

  useEffect(() => {
    const currentRef = contactListRef.current;
    if (currentRef) {
      currentRef.addEventListener('scroll', handleScroll);
      return () => currentRef.removeEventListener('scroll', handleScroll);
    }
  }, [loading]);

  const allCurrentContactsSelected = contacts.length > 0 && 
    contacts.every(contact => selectedContactIds.includes(contact.id));

  return (
    <div className="tw-flex tw-flex-col tw-h-full">
      <div className="tw-p-4 tw-border-b">
        <div className="tw-relative">
          <input
            type="text"
            placeholder="Search members..."
            className="form-control input-field-ui tw-pl-9"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <FiSearch
            className="tw-absolute tw-left-3 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-text-gray-400"
            size={20}
          />
        </div>
        <div className="tw-mt-3">
          <div className="tw-flex tw-justify-between tw-items-center tw-mb-2">
            <span className="tw-text-sm tw-text-gray-500">
              {selectedContactIds.length} selected
            </span>
            <span className="tw-text-sm tw-text-blue-600">
              {/* {contacts.length} total */}
            </span>
          </div>
          
          {/* Select All / Unselect All */}
          {contacts.length > 0 && (
            <div 
              className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-py-2 tw-px-3 tw-bg-gray-50 tw-rounded-md hover:tw-bg-gray-100 tw-transition-colors"
              onClick={allCurrentContactsSelected ? onUnselectAll : onSelectAll}
            >
              <div 
                className={`tw-w-6 tw-h-6 tw-border-2 tw-rounded-md tw-flex tw-items-center tw-justify-center tw-transition-all ${
                  allCurrentContactsSelected
                    ? "tw-bg-[#03335b] tw-border-[#03335b]"
                    : "tw-border-gray-400 tw-bg-gray-100"
                }`}
              >
                {allCurrentContactsSelected && (
                  <FiCheck className="tw-text-white" />
                )}
              </div>
              <span className="tw-text-sm tw-font-medium tw-text-gray-700">
                {allCurrentContactsSelected ? 'Unselect All' : 'Select All'}
              </span>
            </div>
          )}
        </div>
      </div>
      
      <div 
        ref={contactListRef} 
        className="tw-flex-1 tw-overflow-y-auto"
      >
        {contacts.length > 0 ? (
          contacts.map((contact) => (
            <div
              key={contact.id}
              className={`tw-flex tw-items-center tw-px-4 tw-py-3 tw-border-b tw-cursor-pointer hover:tw-bg-gray-100 tw-transition-colors tw-duration-200 ${
                selectedContactIds.includes(contact.id) ? "tw-bg-blue-50" : ""
              }`}
              onClick={() => toggleContactSelection(contact.id, contact.mobile)}
            >
              <div className="tw-flex-1">
                <div className="tw-font-medium">{contact.memberName}</div>
                <div className="tw-text-sm tw-text-gray-500">
                  {contact.mobile || "No mobile"}
                </div>
              </div>
              
              {/* Checkbox - Always visible on the right */}
              <div className="tw-flex-shrink-0">
                <div 
                  className={`tw-w-6 tw-h-6 tw-border-2 tw-rounded-md tw-flex tw-items-center tw-justify-center tw-transition-all ${
                    selectedContactIds.includes(contact.id)
                      ? "tw-bg-[#03335b] tw-border-[#03335b]"
                      : "tw-border-gray-400 tw-bg-gray-100"
                  }`}
                >
                  {selectedContactIds.includes(contact.id) && (
                    <FiCheck className="tw-text-white" />
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="tw-flex tw-flex-col tw-justify-center tw-items-center tw-h-64 tw-text-gray-400">
            <i className="bi bi-people tw-text-5xl tw-mb-2"></i>
            {searchTerm ? "No matching contacts found" : "No contacts available"}
          </div>
        )}
        
        {loading && (
          <div className="tw-flex tw-justify-center tw-py-4">
            <div className="tw-animate-spin tw-inline-block tw-w-6 tw-h-6 tw-border-[3px] tw-border-current tw-border-t-transparent tw-text-gray-400 tw-rounded-full" />
          </div>
        )}
      </div>
    </div>
  );
};

const ConnectGroupMessagePopup = ({ onClose, id, getchat, scrollToBottom, refreshmembers, memberList = [] }) => {
  // const { setLoadingMore, setChatpage, setChatResult } = useInboxContext();

  // States for template selection (copied from ConnectTemplatePopup)
  const [searchTemplate, setSearchTemplate] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [allDataFetched, setAllDataFetched] = useState(false);
  const templateListRef = useRef(null);
  const [messageSent, setMessageSent] = useState(false);
  const [messageSentText, setMssageSentText] = useState("");
  const [isSending, setIsSending] = useState(false);
  const searchTimeoutRef = useRef(null);

  // New states for contact selection
  const [contacts, setContacts] = useState([]);
  const [selectedContactIds, setSelectedContactIds] = useState([]);
  const [selectedMobileNumbers, setSelectedMobileNumbers] = useState([]); 
  const [contactSearchTerm, setContactSearchTerm] = useState("");
  const [contactPage, setContactPage] = useState(1);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [allContactsFetched, setAllContactsFetched] = useState(false);
  const [sentCount, setSentCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [filteredMemberList, setFilteredMemberList] = useState([]);

  // Fetch contacts for the contact list section
  const fetchContacts = async (search = "", page = 1, isNewSearch = false) => {
    if (loadingContacts || (allContactsFetched && !isNewSearch)) return;

    setLoadingContacts(true);
    try {
      const response = await member_list({
        status: "all",
        page: page,
        search: search,
        sort: "Newest"
      });

      if (response.data && response.data.data.list && response.data.data.list.length > 0) {
        const newContacts = response.data.data.list;
        
        if (isNewSearch) {
          setContacts(newContacts);
        } else {
          setContacts((prev) => [...prev, ...newContacts]);
        }
        
        setAllContactsFetched(newContacts.length < 50); // Assuming 50 is page size
      } else {
        setAllContactsFetched(true);
        if (isNewSearch) {
          setContacts([]);
        }
      }
    } catch (error) {
      console.error("Failed to fetch contacts:", error);
    } finally {
      setLoadingContacts(false);
    }
  };

  // Toggle contact selection
  const toggleContactSelection = (contactId, mobile) => {
    let filteredIndex = -1; 
    setSelectedContactIds((prev) => {
      filteredIndex = prev.indexOf(contactId);
      if(filteredIndex !== -1) {
        
        setSelectedMobileNumbers((prev) => {
          const updatedMobileNumbers = [...prev];
          updatedMobileNumbers.splice(filteredIndex, 1);
          return updatedMobileNumbers;
        });
        return prev.filter(id => id !== contactId);
      } else {
        setSelectedMobileNumbers((prev) => [...prev, mobile])
        return [...prev, contactId];
      }
    });
    
    // if(filteredIndex === -1){ 
    //   setSelectedMobileNumbers((prev) => [...prev, mobile])
    // }else{
    //   setSelectedMobileNumbers((prev) => {
    //     const updatedMobileNumbers = [...prev];
    //     updatedMobileNumbers.splice(filteredIndex, 1);
    //     return updatedMobileNumbers;
    //   });
    // }    
  };

  // Select all currently loaded/visible contacts (respects search filters)
  const handleSelectAll = () => {
    // Use the same logic as contactsToDisplay to get currently visible contacts
    const contactsToSelect = memberList.length > 0 
      ? (filteredMemberList.length > 0 || contactSearchTerm.length > 0 
          ? filteredMemberList 
          : memberList
        ).map((member) => ({
          id: member.encUserId,
          mobile: member.mobile
        }))
      : contacts;

    const allIds = contactsToSelect.map(contact => contact.id);
    const allMobiles = contactsToSelect.map(contact => contact.mobile);
    
    setSelectedContactIds(allIds);
    setSelectedMobileNumbers(allMobiles);
  };

  // Unselect all contacts
  const handleUnselectAll = () => {
    setSelectedContactIds([]);
    setSelectedMobileNumbers([]);
  };

  // Load more contacts when scrolling (only for API contacts, not memberList)
  const loadMoreContacts = () => {
    // Only load more if we're using API contacts (memberList is empty)
    if (memberList.length === 0 && !loadingContacts && !allContactsFetched) {
      const nextPage = contactPage + 1;
      setContactPage(nextPage);
      fetchContacts(contactSearchTerm, nextPage);
    } 
  };

  // Function to replace variables in content (from ConnectTemplatePopup)
  const replaceVariables = (content) => {
    if (!content) return "";

    content = content.replace(/[\uFEFF\u200B-\u200D\u2060]/g, '');

    // URL regex for better link detection
    const urlRegex = /(?:(?:https?:\/\/)?(?:www\.)?)?[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z]{2,})+(?:\/[^\s]*)?/gi;

    const commonTLDs = [
      "com",
      "org",
      "net",
      "edu",
      "gov",
      "mil",
      "io",
      "co",
      "ai",
      "app",
      "dev",
      "uk",
      "us",
      "eu",
      "in",
      "de",
      "fr",
    ];

    // First handle line breaks
    content = content.replace(/\\n/g, "<br />");

    // Split content by HTML tags and process each part
    const parts = content.split(/(<[^>]*>)/);
    content = parts
      .map((part) => {
        // If it's an HTML tag, return it unchanged
        if (part.startsWith("<") && part.endsWith(">")) {
          return part;
        }
        // If part contains Arabic/Urdu, wrap it in RTL div
        if (/[\u0600-\u06FF\uFB50-\uFDFF\uFE70-\uFEFF]/.test(part)) {
          return `<div class="tw-text-right" dir="rtl" style="text-align: right;">${part}</div>`;
        }
        // If it's non-empty text without Arabic/Urdu, wrap it in regular div
        if (part.trim()) {
          return `<div>${part}</div>`;
        }
        // Return empty parts unchanged
        return part;
      })
      .join("");

    console.log(content, "content");

    return (
      content
        // Bold: *text* (not preceded or followed by space)
        .replace(/\*([^*]+)\*/g, "<strong>$1</strong>")

        // Italic: _text_ (not preceded or followed by space)
        .replace(/(?<!\s)_(.*?)_(?!\s)/g, "<em>$1</em>")

        // Strikethrough: ~text~ (not preceded or followed by space)
        .replace(/(?<!\s)~(.*?)~(?!\s)/g, "<del>$1</del>")

        // Monospace: ```text```
        .replace(/```(.*?)```/g, "<code>$1</code>")

        // Single line code: `text`
        .replace(/`(.*?)`/g, "<code>$1</code>")

        // Enhanced URL detection and replacement
        .replace(urlRegex, (url) => {
          // Check if this matches common URL patterns
          const isLikelyURL = commonTLDs.some(
            (tld) =>
              url.toLowerCase().includes(`.${tld}`) &&
              url.match(/^[^@\s]+\.[^\s]+$/) // Exclude email addresses
          );

          if (!isLikelyURL) return url;

          // Add protocol if missing
          let fullUrl = url;
          if (!url.match(/^https?:\/\//i)) {
            fullUrl = "https://" + url;
          }

          // Validate URL
          try {
            new URL(fullUrl);
            return `<a href="${fullUrl}" class="tw-text-blue-600 hover:tw-underline" target="_blank" rel="noopener noreferrer">${url}</a>`;
          } catch {
            return url;
          }
        })

        // WhatsApp style quotes: > at start of line
        .replace(
          /(^|\n)&gt; ?([^\n]*)/g,
          '$1<div class="tw-border-l-4 tw-border-gray-300 tw-pl-3 tw-my-1 tw-text-gray-600">$2</div>'
        )

        // Lists: - or • at start of line
        .replace(
          /(^|\n)[-•] ?([^\n]*)/g,
          '$1<div class="tw-flex tw-gap-2 tw-my-1">•<span>$2</span></div>'
        )

        // Handle multiple underscores or asterisks in a single word
        .replace(/(\*{2}|_{2})(.*?)\1/g, "<strong>$2</strong>")
        .replace(/(\*{3}|_{3})(.*?)\1/g, "<strong><em>$2</em></strong>")

        // Fix any potentially broken HTML from nested formatting
        .replace(/<(\/?)(strong|em|del|code)>/g, "<$1$2>")
    );
  };

  // Template mockup component (from ConnectTemplatePopup)
  const TemplateMockup = ({ template }) => {
    return (
      <div className="tw-w-full md:tw-w-[360px] tw-mx-auto">
        <div className="tw-relative tw-h-[600px]">
          <div className="tw-w-full tw-h-full tw-bg-[url('https://appcdn.goqii.com/storeimg/49871_1729598479.png')] tw-bg-contain tw-bg-no-repeat tw-bg-center"></div>

          <div className="tw-absolute tw-top-[85px] tw-left-[25px] tw-right-[25px] tw-bottom-[85px] tw-overflow-y-auto [&::-webkit-scrollbar]:tw-hidden [-ms-overflow-style:'none'] [scrollbar-width:'none']">
            <div className="tw-text-xs tw-text-gray-700 tw-bg-white tw-rounded-lg tw-px-2 tw-w-1/3 tw-text-center tw-mx-auto tw-mb-1 tw-font-semibold">
              Today
            </div>

            <div className="tw-bg-white tw-rounded-lg tw-p-3 tw-shadow-sm">
              <div className="tw-text-gray-600 tw-text-sm tw-mb-3">
                {template?.mediaUrl && (
                  <div className="tw-mb-2">
                    {template.type === "Image" ? (
                      <img
                        src={template.mediaUrl}
                        alt={template.templateName}
                        className="tw-w-full tw-h-full tw-object-cover tw-rounded"
                      />
                    ) : template.type === "Video" ? (
                      <video className="tw-w-full tw-h-full tw-rounded" controls>
                        <source src={template.mediaUrl} type="video/mp4" />
                      </video>
                    ) : template.type === "Document" ? (
                      <div className="tw-bg-gray-100 tw-p-3 tw-rounded tw-flex tw-items-center tw-gap-2">
                        <svg
                          className="tw-w-6 tw-h-6"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                          />
                        </svg>
                        <span className="tw-text-sm">Document attached</span>
                      </div>
                    ) : null}
                  </div>
                )}

                {template.headerContent && (
                  <div className="tw-font-medium tw-mb-2">
                    {template.headerContent}
                  </div>
                )}

                <p
                  className="tw-whitespace-pre-wrap"
                  dangerouslySetInnerHTML={{
                    __html: replaceVariables(template.templateBody),
                  }}
                ></p>
              </div>

              {template.footerContent && (
                <p className="tw-text-sm tw-text-gray-500 tw-mb-2">
                  {template.footerContent}
                </p>
              )}

              {template.buttons && template.buttons.length > 0 && (
                <div className="tw-flex tw-flex-col tw-gap-px tw-mt-3 tw-mx-[-12px] tw-mb-[-12px]">
                  {template.buttons.map((button, index) => (
                    <span
                      key={index}
                      className={`
          tw-text-[#1c4cba] dark:tw-text-[#4d82ff]
          tw-text-sm
          tw-font-medium
          tw-py-3
          tw-px-4
          tw-text-center
          tw-bg-white
          hover:tw-bg-gray-50
          active:tw-bg-gray-100
          tw-transition-colors
          tw-border-t
          tw-border-gray-200
          first:tw-border-t-0
          ${index === 0 ? "tw-rounded-b-lg" : ""}
          tw-cursor-pointer
          tw-flex
          tw-items-center
          tw-justify-center
          tw-gap-2
          tw-w-full
        `}
                    >
                      {button.type === "QUICK_REPLY" ? (
                        <span className="tw-truncate">{button.title}</span>
                      ) : null}
                    </span>
                  ))}
                </div>
              )}

              {template.websiteUrl && template.websiteUrl.length > 0 && (
                <div className="tw-flex tw-flex-col tw-gap-px tw-mt-3 tw-mx-[-12px] tw-mb-[-12px]">
                  {template.websiteUrl.map((link, index) => (
                    <span
                      key={index}
                      className={`
          tw-text-[#1c4cba] dark:tw-text-[#4d82ff]
          tw-text-sm
          tw-font-medium
          tw-py-3
          tw-px-4
          tw-text-center
          tw-bg-white
          hover:tw-bg-gray-50
          active:tw-bg-gray-100
          tw-transition-colors
          tw-border-t
          tw-border-gray-200
          first:tw-border-t-0
          ${index === 0 ? "tw-rounded-b-lg" : ""}
          tw-cursor-pointer
          tw-flex
          tw-items-center
          tw-justify-center
          tw-gap-2
          tw-w-full
        `}
                    >
                      {link.type === "URL" && (
                        <>
                          <svg
                            className="tw-w-4 tw-h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                            />
                          </svg>
                          <span className="tw-truncate">{link.title}</span>
                        </>
                      )}
                      {link.type === "PHONE_NUMBER" && (
                        <>
                          <svg
                            className="tw-w-4 tw-h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                            />
                          </svg>
                          <span className="tw-truncate">{link.title}</span>
                        </>
                      )}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Fetch templates function (from ConnectTemplatePopup)
  const fetchTemplates = async (
    searchText = "",
    pageNumber = 1,
    isNewSearch = false
  ) => {
    if (loading || (allDataFetched && !isNewSearch)) return;

    setLoading(true);
    setIsFetching(true);

    try {
      // Using the ID from props, or the first selected contact if any
      const userId = id || (selectedContactIds.length > 0 ? selectedContactIds[0] : "dummy");
      
      const response = await whatsapp_list({
        userId: userId,
        search: searchText,
        page: pageNumber,
      });

      if (response.data && response.data.data.templateData.length > 0) {
        const newTemplates = response.data.data.templateData;

        if (isNewSearch) {
          // For new searches, replace existing templates
          setTemplates(newTemplates);
        } else {
          // For pagination, append to existing templates
          setTemplates((prev) => [...prev, ...newTemplates]);
        }

        setAllDataFetched(newTemplates.length < 50); // Assuming 50 is the page size
      } else {
        setAllDataFetched(true);
        if (isNewSearch) {
          setTemplates([]); // Clear templates if it's a new search with no results
        }
      }
    } catch (error) {
      console.error("Failed to fetch templates:", error);
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const searchValue = e.target.value;
    setSearchTemplate(searchValue);

    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for search
    searchTimeoutRef.current = setTimeout(() => {
      setPage(1);
      setAllDataFetched(false);
      fetchTemplates(searchValue, 1, true);
    }, 500); // 500ms debounce
  };

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Initial load for templates
  useEffect(() => {
    fetchTemplates("", 1, true);
  }, []);

  // Initial load for contacts
  useEffect(() => {
    // Only fetch from API if memberList is empty
    if (memberList.length === 0) {
      fetchContacts("", 1, true);
    } else {
      // Initialize filteredMemberList with all members
      setFilteredMemberList(memberList);
    }
  }, []); // Run only once on mount

  // Handle contact search
  useEffect(() => {
    const timer = setTimeout(() => {
      // If memberList has members, search locally
      if (memberList.length > 0) {
        // Local search - no API call needed
        if (contactSearchTerm.length > 0) {
          const filtered = memberList.filter(member => 
            member.memberName?.toLowerCase().includes(contactSearchTerm.toLowerCase()) ||
            member.mobile?.includes(contactSearchTerm)
          );
          setFilteredMemberList(filtered);
        } else {
          // No search term, show all members
          setFilteredMemberList(memberList);
        }
      } else {
        // No memberList, use API search (only when search term changes)
        if (contactSearchTerm.length > 0) {
          setContactPage(1);
          setAllContactsFetched(false);
          fetchContacts(contactSearchTerm, 1, true);
        } else {
          // When search is cleared and memberList is empty, reset to initial API fetch
          setContactPage(1);
          setAllContactsFetched(false);
          fetchContacts("", 1, true);
        }
      }
    }, 500);
    
    return () => clearTimeout(timer);
  }, [contactSearchTerm]); // Only depend on contactSearchTerm, not memberList

  // Handle template list scroll
  const handleScroll = () => {
    if (templateListRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = templateListRef.current;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 20;

      if (isNearBottom && !loading && !isFetching && !allDataFetched) {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchTemplates(searchTemplate, nextPage, false);
      }
    }
  };

  useEffect(() => {
    const listElement = templateListRef.current;
    if (listElement) {
      listElement.addEventListener("scroll", handleScroll);
      return () => listElement.removeEventListener("scroll", handleScroll);
    }
  }, [loading, isFetching, allDataFetched, page, searchTemplate]);

  // Send template to selected contacts
  const handleSendTemplate = async () => {
    if (!selectedTemplate || selectedContactIds.length === 0 || isSending) return;

    try {
      setIsSending(true);
      setSentCount(0);
      setFailedCount(0);
      let response; 

      try{ 
        response = await group_whatsapp_send({
          userIds: JSON.stringify(selectedContactIds),
          templateId: selectedTemplate.templateId, 
          mobileNos: JSON.stringify(selectedMobileNumbers)
        }); 

        if(response.data.code === 200){ 
            // success
            setMssageSentText("Message Sent Successfully.");  
            setMessageSent(true);
            setSentCount(1); 
            setTimeout(() => { 
              onClose(); 
              refreshmembers(); 
            }, 1000); 
        }else { 
          setMssageSentText("Some Error Occured while sending Tempalte, Please try After Some time");
        }
      }catch(e) {
        setMssageSentText("Some Error Occured while sending Tempalte, Please try After Some time");
      }; 
      
      // Process sending to each selected contact
      // const results = await Promise.allSettled(
      //   selectedContactIds.map(contactId => 
      //     sendTemplateApi({
      //       userId: contactId,
      //       templateId: selectedTemplate.templateId,
      //       templateBody: ""
      //     })
      //   )
      // );
      
      // Count successes and failures
      // let successes = 0;
      // let failures = 0;
      
      // results.forEach(result => {
      //   if (result.status === 'fulfilled' && result.value.data.code === 200) {
      //     successes++;
      //   } else {
      //     failures++;
      //   }
      // });
      
      // setSentCount(successes);
      // setFailedCount(failures);
      
      // if (successes > 0) {
      //   setMssageSentText(`Successfully sent to ${successes} contacts${failures > 0 ? `, failed for ${failures} contacts` : ''}`);
      // } else {
      //   setMssageSentText("Failed to send templates. Please try again.");
      // }

      // scrollToBottom();
      
      // // Refresh chat if any message was sent successfully
      // if (successes > 0) {
      //   setChatpage(1);
      //   setChatResult([]);
      //   setLoadingMore(true);
      // }
      
      // // Close the popup after a delay
      // setTimeout(() => {
      //   setMessageSent(false);
      //   if (successes > 0) {
      //     onClose();
      //   }
      // }, 3000);
      
    } catch (error) {
      console.error("Failed to send templates:", error);
      setMssageSentText("An error occurred while sending templates");
      setMessageSent(true);
      setTimeout(() => {
        setMessageSent(false);
      }, 3000);
    } finally {
      setIsSending(false);
    }
  };

  
  const contactsToDisplay = memberList.length > 0 
    ? (filteredMemberList.length > 0 || contactSearchTerm.length > 0 
        ? filteredMemberList 
        : memberList
      ).map((member) => ({
        id: member.encUserId,
        memberName: member.memberName,
        mobile: member.mobile
      }))
    : contacts; 

  return ReactDOM.createPortal(
    <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-[99999]">
      <div className="tw-bg-white tw-rounded-lg tw-overflow-hidden tw-w-[90vw] tw-h-[90vh] tw-flex tw-flex-col">
    {/* Rest of the component remains the same */}
    
    {/* Header with improved z-index */}
    <div className="tw-flex tw-justify-between tw-items-center tw-p-4 tw-border-b tw-bg-white tw-sticky tw-top-0 tw-z-10">
      <h2 className="tw-text-xl tw-font-bold tw-text-gray-600">
        Bulk Message
      </h2>
      <span
        className="tw-cursor-pointer tw-text-xl tw-font-bold tw-mb-1.5"
        onClick={onClose}
      >
        <FiX />
      </span>
    </div>

    {/* Content container with explicit viewport constraints */}
    <div className="tw-flex tw-flex-grow tw-overflow-hidden tw-h-[calc(100%-110px)]">
      {/* Section 1: Contact list with proper overflow handling */}
      <div className="tw-w-1/3 tw-border-r tw-flex tw-flex-col tw-h-full tw-overflow-hidden">
        <div className="tw-p-4 tw-pb-0 tw-border-b tw-bg-white tw-sticky tw-top-0 tw-z-10">
          <h5 className="tw-font-bold tw-text-gray-600">Select Recipients</h5>
        </div>
        <div className="tw-flex-1 tw-overflow-y-auto">
          <ContactSelectionList
            contacts={contactsToDisplay}
            selectedContactIds={selectedContactIds}
            toggleContactSelection={toggleContactSelection}
            searchTerm={contactSearchTerm}
            setSearchTerm={setContactSearchTerm}
            loadMoreContacts={loadMoreContacts}
            loading={loadingContacts}
            onSelectAll={handleSelectAll}
            onUnselectAll={handleUnselectAll}
          />
        </div>
      </div>

      <div className="vr"></div>

      {/* Section 2: Template list with proper overflow handling */}
      <div className="tw-w-1/3 tw-border-r tw-flex tw-flex-col tw-h-full tw-overflow-hidden">
        <div className="tw-p-4 tw-sticky tw-top-0 tw-bg-white tw-z-10">
          <h5 className="tw-font-bold tw-text-gray-600 tw-mb-6">Select Template</h5>
          {/* <div className="form-group" style={{ width: "100%" }}>
            <input
              type="text"
              placeholder="Search Template"
              className="form-control input-field-ui ps-5"
              value={searchTemplate}
              onChange={handleSearchChange}
              disabled={selectedContactIds.length === 0}
            />
            <img
              src="https://storage.googleapis.com/ksabupatest/2024/09/24/ljce8/fac3csq13o.png"
              className="input-icon-seacrh"
              alt=""
            />
          </div> */}
          <div className="tw-relative">
          <input
            type="text"
              placeholder="Search Template"
            className="form-control input-field-ui tw-pl-9"
            value={searchTemplate}
              onChange={handleSearchChange}
              disabled={selectedContactIds.length === 0}
          />
          <FiSearch
            className="tw-absolute tw-left-3 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-text-gray-400"
            size={20}
          />
        </div>
        </div>
        <div ref={templateListRef} className="tw-flex-1 tw-overflow-y-auto tw-p-4 tw-pt-0">
          {selectedContactIds.length === 0 ? (
            <div className="tw-text-center tw-text-gray-500 tw-py-4">
              Select at least one recipient to view templates
            </div>
          ) : templates.length === 0 && !loading ? (
            <div className="tw-text-center tw-text-gray-500 tw-py-4">
              No templates found
            </div>
          ) : (
            templates.map((template, index) => (
              <div
                key={`${template.templateId}-${index}`}
                className={`tw-p-2 tw-cursor-pointer tw-border-b tw-transition-all tw-duration-300 hover:tw-bg-gray-100 
                                        ${selectedTemplate?.templateId === template.templateId ? "tw-bg-[#f1f6fc]" : ""}`}
                onClick={() => setSelectedTemplate(template)}
              >
                {template.templateName}
              </div>
            ))
          )}
          {loading && (
            <div className="tw-text-center tw-py-4">
              <div className="tw-animate-spin tw-inline-block tw-w-6 tw-h-6 tw-border-[3px] tw-border-current tw-border-t-transparent tw-text-gray-400 tw-rounded-full" />
            </div>
          )}
        </div>
      </div>

      <div className="vr"></div>

      {/* Section 3: Template details with proper overflow handling */}
      {/* <div className="tw-w-2/5 tw-border-r tw-h-full tw-overflow-y-auto">
        <div className="tw-sticky tw-top-0 tw-bg-white tw-z-10 tw-p-4 tw-border-b">
          <h5 className="tw-font-bold">Template Details</h5>
        </div>
        <div className="tw-p-4 tw-pt-0">
          {selectedTemplate ? (
            <div className="tw-overflow-y-auto tw-pr-2">
              <div className="tw-bg-gray-50 tw-rounded-lg tw-p-4">
            
                  <h5 className="tw-font-bold tw-mb-4">Message</h5>
                  <div className="tw-overflow-y-auto tw-pr-2">
                    <div className="tw-bg-gray-50 tw-rounded-lg tw-p-4">
                      <div className="tw-mb-4">
                        <p className="tw-font-semibold tw-text-gray-700 tw-mb-1">
                          Body:
                        </p>
                        <div className="tw-bg-white tw-p-3 tw-rounded tw-border tw-border-gray-200">
                          <div
                            className="tw-text-sm tw-break-words tw-whitespace-pre-wrap"
                            dangerouslySetInnerHTML={{
                              __html: replaceVariables(
                                selectedTemplate.templateBody
                              ),
                            }}
                          />
                        </div>
                      </div>

                      <div className="tw-mb-4">
                        <p className="tw-font-semibold tw-text-gray-700 tw-mb-1">
                          Buttons:
                        </p>
                        <div className="tw-bg-white tw-p-3 tw-rounded tw-border tw-border-gray-200">
                          {selectedTemplate.buttons &&
                          selectedTemplate.buttons.length > 0 ? (
                            <div className="tw-space-y-2">
                              {selectedTemplate.buttons.map((button, index) => (
                                <p
                                  key={index}
                                  className="tw-text-sm tw-break-words"
                                >
                                  {button.type}: {button.title}
                                </p>
                              ))}
                            </div>
                          ) : (
                            <p className="tw-text-sm tw-break-words">
                              No buttons
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="tw-mb-4">
                        <p className="tw-font-semibold tw-text-gray-700 tw-mb-1">
                          Website URLs & Phone Numbers:
                        </p>
                        <div className="tw-bg-white tw-p-3 tw-rounded tw-border tw-border-gray-200">
                          {selectedTemplate.websiteUrl &&
                          selectedTemplate.websiteUrl.length > 0 ? (
                            <div className="tw-space-y-2">
                              {selectedTemplate.websiteUrl.map(
                                (link, index) => (
                                  <p
                                    key={index}
                                    className="tw-text-sm tw-break-words"
                                  >
                                    {link.type === "URL"
                                      ? `URL: ${link.title} (${link.url})`
                                      : link.type === "PHONE_NUMBER"
                                      ? `Phone: ${link.title} (${link.phone})`
                                      : ""}
                                  </p>
                                )
                              )}
                            </div>
                          ) : (
                            <p className="tw-text-sm tw-break-words">
                              No website URLs or phone numbers
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="tw-mb-4">
                        <p className="tw-font-semibold tw-text-gray-700 tw-mb-1">
                          Header Content:
                        </p>
                        <div className="tw-bg-white tw-p-3 tw-rounded tw-border tw-border-gray-200">
                          <p className="tw-text-sm tw-break-words">
                            {selectedTemplate.headerContent ||
                              "No header content"}
                          </p>
                        </div>
                      </div>

                      <div className="tw-mb-4">
                        <p className="tw-font-semibold tw-text-gray-700 tw-mb-1">
                          Media URL:
                        </p>
                        <div className="tw-bg-white tw-p-3 tw-rounded tw-border tw-border-gray-200">
                          <p className="tw-text-sm tw-break-all hover:tw-whitespace-normal">
                            {selectedTemplate.mediaUrl || "No media URL"}
                          </p>
                        </div>
                      </div>

                      <div>
                        <p className="tw-font-semibold tw-text-gray-700 tw-mb-1">
                          Category:
                        </p>
                        <div className="tw-bg-white tw-p-3 tw-rounded tw-border tw-border-gray-200">
                          <p className="tw-text-sm tw-break-words">
                            {selectedTemplate.category || "No category"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                
              </div>
            </div>
          ) : (
            <div className="tw-text-center tw-text-gray-500 tw-py-4">
              Select a template to see details
            </div>
          )}
        </div>
      </div> */}

      {/* <div className="vr"></div> */}

      {/* Section 4: Template preview with proper overflow handling */}
      <div className="tw-w-1/3 tw-h-full tw-overflow-y-auto">
        <div className="tw-sticky tw-top-0 tw-bg-white tw-z-10 tw-pl-2 tw-border-b">
          <h5 className="tw-font-bold">Preview</h5>
        </div>
        <div className=" tw-pt-0 tw-p-2">
          {selectedTemplate ? (
            <TemplateMockup template={selectedTemplate} />
          ) : (
            <div className="tw-text-center tw-text-gray-500 tw-py-4">
              Select a template to see preview
            </div>
          )}
        </div>
      </div>
    </div>

    {/* Footer with improved z-index */}
    <div className="tw-p-4 tw-border-t tw-flex tw-justify-between tw-items-center tw-sticky tw-bottom-0 tw-bg-white tw-z-10">
      <div className="tw-text-sm tw-text-gray-600">
        {selectedContactIds.length > 0
          ? `${selectedContactIds.length} recipient${selectedContactIds.length > 1 ? 's' : ''} selected`
          : "No recipients selected"}
      </div>
      
      {!messageSent && (
        <span 
          className={`
            tw-bg-[#03335b] 
            tw-text-white 
            tw-px-6 
            tw-py-2 
            tw-rounded 
            tw-flex 
            tw-items-center 
            tw-justify-center 
            tw-min-w-[180px] 
            ${(!selectedTemplate || selectedContactIds.length === 0 || isSending) 
              ? 'tw-opacity-50 tw-cursor-not-allowed tw-bg-gray-400 hover:tw-bg-gray-400' 
              : 'hover:tw-bg-[#1279BE] tw-transition-all tw-duration-300 tw-cursor-pointer'
            }
          `}
          onClick={handleSendTemplate}
          disabled={!selectedTemplate || selectedContactIds.length === 0 || isSending}
        >
          {isSending ? (
            <div className="tw-flex tw-items-center tw-gap-2">
              <svg className="tw-animate-spin tw-h-5 tw-w-5 tw-text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Sending...</span>
            </div>
          ) : (
            'Send to selected recipients'
          )}
        </span>
      )}
    </div>
    
    {/* Success/error message with improved positioning and z-index */}
    {messageSent && (
      <div className={`tw-bottom-16 tw-mx-auto tw-w-full tw-text-center tw-rounded tw-flex tw-items-center tw-p-2 tw-justify-center tw-gap-2 tw-z-20 ${
        sentCount > 0 ? 'tw-bg-green-500 tw-text-white' : 'tw-bg-red-500 tw-text-white'
      }`}>
        <svg className="tw-h-5 tw-w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
        </svg>
        <span>{messageSentText}</span>
      </div>
    )}
  </div>
</div>,
document.body
  );
};

export default ConnectGroupMessagePopup;